import { BatteryMedium, ShieldCheck, ShieldPlus } from "lucide-react";
import { warrantyOf, categoryOf, accTypeOf, usesModels, ACC_TYPES, CATEGORIES, extraSpecs } from "@/lib/store";
import { PriceBlock } from "./PriceBlock";
import { InstallmentSimulator } from "./InstallmentSimulator";
import { HotBadge } from "./HotBadge";
import { InterestAction } from "./InterestAction";
import { ShareButton } from "./ShareButton";
import { WatchPriceButton } from "./WatchPriceButton";
import { VariantSwitcher } from "./VariantSwitcher";
import { ConditionChip } from "./Condition";
import { PartsInfo } from "./PartsInfo";
import { DeliveryCheck } from "./DeliveryCheck";

const Chip = ({ children }) => <span className="rounded-full border border-white/15 px-3 py-1 text-sm text-zinc-200">{children}</span>;

export const ProductDetails = ({ p, all = [] }) => {
  const isAcc = categoryOf(p) === "acessorios";
  const type = ACC_TYPES.find((t) => t.id === accTypeOf(p));
  const sold = p.status === "vendido";
  const chips = isAcc
    ? ["HPrime", type?.single, accTypeOf(p) === "fonte" ? p.wattage : null, p.color]
    : [p.storage, ...extraSpecs(p), p.color];

  return (
    <div data-testid="product-details" className="flex flex-col">
      <p className="mono hidden text-[11px] uppercase tracking-[0.28em] text-zinc-400 sm:block">{CATEGORIES.find((c) => c.id === categoryOf(p))?.label}</p>
      <HotBadge id={p.id} size="lg" className="mb-3 w-fit sm:mt-4 sm:mb-0" />
      <h1 data-testid="product-page-title" className="font-display break-words text-4xl font-extrabold leading-[0.95] tracking-[-0.04em] text-white sm:mt-4 sm:text-6xl">{p.model}</h1>
      <div className="mt-4 flex flex-wrap gap-2 sm:mt-6">
        {chips.filter(Boolean).map((c) => <Chip key={c}>{c}</Chip>)}
        {!isAcc && <ConditionChip p={p} testid="product-page-condition" />}
        {p.battery != null && !isAcc && <span className="flex items-center gap-1.5 rounded-full border border-emerald-300/30 px-3 py-1 text-sm text-emerald-300"><BatteryMedium className="h-4 w-4" /> Bateria {p.battery}%</span>}
      </div>
      <div className="mt-5 sm:mt-7">
        <PriceBlock p={p} size="page" priceTestid="product-page-price" installmentTestid="product-page-installment" />
        {!isAcc && <InstallmentSimulator p={p} />}
      </div>
      {!isAcc && <PartsInfo p={p} />}
      <VariantSwitcher product={p} all={all} />
      {isAcc && usesModels(accTypeOf(p)) && p.compatible?.length === 1 && (
        <p className="mt-5 text-zinc-300"><span className="text-zinc-400">Disponível para: </span>{p.compatible.join(", ")}</p>
      )}
      <p data-testid="product-page-warranty" className="mt-5 flex items-center gap-2 font-medium text-emerald-300"><ShieldCheck className="h-5 w-5 shrink-0" /> {warrantyOf(p)}</p>
      {isAcc && p.accidental && (
        <div className="mt-3 rounded-2xl border border-amber-300/30 bg-amber-300/10 px-4 py-3">
          <p className="flex items-center gap-2 font-semibold text-amber-200"><ShieldPlus className="h-4 w-4" /> Quebra acidental · 6 meses</p>
          <p className="mt-0.5 pl-6 text-sm text-zinc-300">Protege caso o aparelho quebre usando este acessório.</p>
        </div>
      )}
      {p.notes && <p className="mt-6 max-w-lg text-zinc-300 leading-relaxed">{p.notes}</p>}
      {!sold && <DeliveryCheck item={{ category: categoryOf(p), price: Number(p.price) || 0, quantity: 1 }} />}

      <div id="product-actions" className="mt-8 space-y-3">
        {sold ? (
          <p data-testid="product-page-sold" className="rounded-full border border-white/20 py-3.5 text-center font-semibold text-white">Este produto já foi vendido</p>
        ) : (
          <InterestAction p={p} />
        )}
        <ShareButton p={p} />
        {p.status !== "vendido" && <WatchPriceButton p={p} />}
      </div>
    </div>
  );
};
